import React, { useState } from 'react'
import CreateOrganizationPopupContent from '../CreateOrganizationPopupContent'
import CreateOrganizationPopupTitle from '../CreateOrganizationPopupTitle'
import CreateOrganizationPopupFormActions from '../CreateOrganizationPopupFormActions'
import { ButtonOutline } from '../../../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import errorToast from '../../../../Components/Toasts/ErrorToast'
import successToast from '../../../../Components/Toasts/SuccessToast'
import { isValidEmail } from '../../../../Helpers/isValidEmail'
import { useCreateOrganizationUser } from '../../hooks/useCreateOrganizationUser'
import { useChangeOrganizationUsersRole } from '../../hooks/useChangeOrganizationUsersRole'
import { useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import AddMembersPopupFormStep from '../AddMembersPopupFormStep'
import { useGetOrgUsers } from '../../hooks/useGetOrgUsers'
import { useAddMemberStore } from '../../../../Storage/organizationsPage/useAddMemberStore'

export default function AddMembersPopupForm({
  organization,
  closePopup,
  groups,
}) {
  const [data, setData] = useState({
    owners: [],
  })
  const isErrorAfterSubmit = useAddMemberStore(state => state.isError)
  const setIsErrorAfterSubmit = useAddMemberStore(state => state.setIsError)

  const o_f_id = organization.f_id
  const g_f_id = groups[0].f_id
  const queryClient = useQueryClient()
  const { data: orgUsers } = useGetOrgUsers({ o_f_id })
  const {
    mutateAsync: changeOrganizationUsersRole,
    isPending: isLoadingChangeOrganizationUsersRole,
  } = useChangeOrganizationUsersRole()

  const {
    mutateAsync: createOrganizationUser,
    isPending: isLoadingCreateOrganizationUser,
  } = useCreateOrganizationUser()

  const addContent = arr => {
    try {
      return Promise.allSettled(arr)
    } catch (error) {
      console.error('Error adding content:', error)
    }
  }

  const createUsersOneByOne = async ({ users, o_f_id }) => {
    try {
      return await Promise.allSettled(
        users.map(user =>
          createOrganizationUser({
            o_f_id,
            email: user.email,
          }).then(data => {
            return changeOrganizationUsersRole({
              o_f_id,
              g_f_id,
              role:
                user.role === 'Organization Admin'
                  ? 'Organization Administrator'
                  : user.role,
              _action: 'add',
              organization_users: [data.f_id],
            })
          })
        )
      )
    } catch (error) {
      console.error('Error creating users:', error)
    }
  }

  const onSubmit = e => {
    e.preventDefault()
    const { owners } = data

    const newOwners = [
      ...new Map(owners.map(item => [item.email, item]))
        .values()
        .filter(item => item.email),
    ]

    const isValidNewOwnersEmail = newOwners.every(item =>
      isValidEmail(item.email)
    )

    const isValidNewOwnersRoles = newOwners.every(item => item.role)
    setIsErrorAfterSubmit(!isValidNewOwnersRoles)
    const existUser = owners
      .map(owner => owner.email.toLowerCase())
      .filter(email =>
        orgUsers.organization_users.some(
          user => user.email.toLowerCase() === email
        )
      )

    if (!newOwners.length) {
      errorToast('At least one Organization member is required')
      return
    }
    if (!isValidNewOwnersEmail) {
      errorToast('Organization member email must be valid email')
      return
    }
    if (!isValidNewOwnersRoles) {
      errorToast(
        'In order to add a member, you must assign them to a specific role.'
      )
      return
    }
    if (existUser.length) {
      existUser.forEach(user => {
        errorToast(`The user ${user} is already in the organization`)
      })
      return
    }

    const arr = [
      newOwners.length
        ? createUsersOneByOne({
            users: newOwners,
            o_f_id,
          })
        : null,
    ]

    addContent(arr)
      .then(() => {
        successToast('Organization members added successfully')
        queryClient.invalidateQueries([
          'organization',
          'details',
          organization.f_id,
        ])
      })
      .then(() => closePopup())
  }

  return (
    <CreateOrganizationPopupContent onSubmit={onSubmit}>
      <CreateOrganizationPopupTitle
        title={`Add Member to ${organization.name}`}
        subtitle={`Organization members have access to all artists within the ${organization.name} organization`}
      />

      <AddMembersPopupFormStep
        setData={setData}
        data={data}
        type="organization"
        label="Organization Member"
      />

      <CreateOrganizationPopupFormActions>
        <ButtonOutline
          color={'pink'}
          size="small"
          text={'INVITE'}
          type="submit"
          disable={
            isLoadingChangeOrganizationUsersRole ||
            isLoadingCreateOrganizationUser
          }
        />
      </CreateOrganizationPopupFormActions>
    </CreateOrganizationPopupContent>
  )
}

AddMembersPopupForm.propTypes = {
  organization: PropTypes.object,
  groups: PropTypes.array,
  closePopup: PropTypes.func,
}
